import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import AppNav from "../components/appNav";
import Footer from "../components/footer";
import { useTranslation } from "react-i18next";

const Section = ({
  num,
  title,
  children,
}: {
  num: string;
  title?: string;
  children: React.ReactNode;
}) => (
  <section className="swiss-section">
    <div className="swiss-grid">
      <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
        <span className="swiss-num">{num}</span>
      </div>
      <div className="col-span-12 lg:col-span-10">
        {title && (
          <h2 className="text-h2 font-bold tracking-tight text-ink mb-7 max-w-[22ch]">{title}</h2>
        )}
        {children}
      </div>
    </div>
  </section>
);

const MVPBuilderProgramPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>KODE MVP Builder Program | 8 Weeks: Build Your MVP + Learn to Evolve It</title>
        <meta name="description" content="8-week intensive program. Build your MVP in 4 weeks, then learn to evolve it." />
      </Helmet>

      <div className="min-h-screen bg-paper text-ink">
        <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
          <AppNav />
        </header>

        <main>
          <section className="swiss-section !border-t-0 pt-7 lg:pt-9">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">01 / MVP</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <Link to="/mvp-build" className="swiss-caption link-underline text-ink mb-7 inline-block">
                  ← {t('mvpBuilderProgram.backToMvp')}
                </Link>
                <h1 className="text-[clamp(2.5rem,5.5vw,5.5rem)] font-black tracking-tightest leading-[0.95] text-ink max-w-[16ch] mb-7 mt-5">
                  {t('mvpBuilderProgram.hero.title')}
                </h1>
                <p className="text-body-lg text-ink-70 max-w-prose mb-5">
                  {t('mvpBuilderProgram.hero.subtitle')}
                </p>
                <p className="text-body text-ink-70 max-w-prose">
                  {t('mvpBuilderProgram.hero.description')}
                </p>
              </div>
            </div>
          </section>

          <Section num={`02 / ${t('Parts')}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(["part1", "part2"] as const).map((part, i) => (
                <div key={part} className="border-t border-ink pt-4">
                  <span className="swiss-caption mb-7 block">
                    {String(i + 1).padStart(2, "0")} / {t(`mvpBuilderProgram.parts.${part}.subtitle`)}
                  </span>
                  <h3 className="text-h3 font-medium tracking-tight text-ink mb-5">
                    {t(`mvpBuilderProgram.parts.${part}.title`)}
                  </h3>
                  <ul className="flex flex-col gap-2 text-body text-ink-70 mb-5">
                    {["item1", "item2", "item3", "item4"].map((k) => (
                      <li key={k} className="flex gap-3">
                        <span className="text-ink-50">—</span>
                        <span>{t(`mvpBuilderProgram.parts.${part}.${k}`)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-body font-medium text-ink border-t border-ink-14 pt-4">
                    {t(`mvpBuilderProgram.parts.${part}.result`)}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          <Section num={`03 / ${t('Fit')}`} title={t('mvpBuilderProgram.whoIsItFor.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(["perfectFor", "notFor"] as const).map((k) => (
                <div key={k} className="border-t border-ink pt-4">
                  <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                    {t(`mvpBuilderProgram.whoIsItFor.${k}.title`)}
                  </h3>
                  <ul className="flex flex-col gap-2 text-body text-ink-70">
                    {(k === "perfectFor" ? [1, 2, 3, 4, 5] : [1, 2, 3, 4]).map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-ink-50">{k === "perfectFor" ? "+" : "—"}</span>
                        <span>{t(`mvpBuilderProgram.whoIsItFor.${k}.item${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          <Section num={`04 / ${t('Schedule')}`} title={t('mvpBuilderProgram.weeklyBreakdown.title')}>
            {[
              { label: t('mvpBuilderProgram.weeklyBreakdown.buildingPhase'), weeks: [1, 2, 3, 4] },
              { label: t('mvpBuilderProgram.weeklyBreakdown.trainingPhase'), weeks: [5, 6, 7, 8] },
            ].map((phase) => (
              <div key={phase.label} className="mb-9 last:mb-0">
                <span className="swiss-caption block mb-5">{phase.label}</span>
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {phase.weeks.map((week) => (
                    <li key={week} className="border-t border-ink pt-4">
                      <span className="swiss-num mb-5 block">{String(week).padStart(2, "0")}</span>
                      <h4 className="text-h4 font-medium tracking-tight text-ink mb-3">
                        {t(`mvpBuilderProgram.weeklyBreakdown.week${week}.title`)}
                      </h4>
                      <p className="text-body text-ink-70">
                        {t(`mvpBuilderProgram.weeklyBreakdown.week${week}.description`)}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Section>

          <Section num={`05 / ${t('Cohort')}`} title={t('mvpBuilderProgram.firstCohort.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-7">
              <div className="border-t border-ink pt-4">
                <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                  {t('mvpBuilderProgram.firstCohort.details.title')}
                </h3>
                <ul className="flex flex-col gap-3 text-body text-ink-70">
                  <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.dates')}</span> 13 Jan – 8 Mar 2025</li>
                  <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.participants')}</span> {t('mvpBuilderProgram.firstCohort.details.maxFounders')}</li>
                  <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.modality')}</span> {t('mvpBuilderProgram.firstCohort.details.modalityDesc')}</li>
                  <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.sessions')}</span> {t('mvpBuilderProgram.firstCohort.details.sessionsDesc')}</li>
                </ul>
              </div>
              <div className="border-t border-ink pt-4">
                <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                  {t('mvpBuilderProgram.firstCohort.includes.title')}
                </h3>
                <ul className="flex flex-col gap-2 text-body text-ink-70">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-ink-50">—</span>
                      <span>{t(`mvpBuilderProgram.firstCohort.includes.item${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="border-t border-ink pt-4 flex flex-wrap items-baseline gap-6">
              <p className="text-[clamp(2.5rem,5vw,4rem)] font-black tracking-tighter leading-none text-ink">
                {t('mvpBuilderProgram.firstCohort.price')}
              </p>
              <p className="text-body text-ink-70">{t('mvpBuilderProgram.firstCohort.priceNote')}</p>
              <p className="swiss-caption text-accent">{t('mvpBuilderProgram.firstCohort.spotsAvailable')}</p>
            </div>
          </Section>

          <Section num={`06 / ${t('Method')}`} title={t('mvpBuilderProgram.whyItWorks.title')}>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(["buildTogether", "realCode", "contextualLearning", "continuousRelationship"] as const).map((k) => (
                <li key={k} className="border-t border-ink pt-4">
                  <h4 className="text-h4 font-medium tracking-tight text-ink mb-3">
                    {t(`mvpBuilderProgram.whyItWorks.${k}.title`)}
                  </h4>
                  <p className="text-body text-ink-70">
                    {t(`mvpBuilderProgram.whyItWorks.${k}.description`)}
                  </p>
                </li>
              ))}
            </ul>
          </Section>

          <section className="swiss-section">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">07 / {t('Next')}</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <h2 className="text-h2 font-bold tracking-tight text-ink mb-5 max-w-[22ch]">
                  {t('mvpBuilderProgram.cta.title')}
                </h2>
                <p className="text-body-lg text-ink-70 max-w-prose mb-7">
                  {t('mvpBuilderProgram.cta.description')}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://wa.me/34636349614"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    {t('mvpBuilderProgram.cta.button')}
                  </a>
                  <a href="mailto:mvp@kode.zone" className="btn btn-secondary">
                    {t('mvpBuilderProgram.cta.email')}
                  </a>
                </div>
                <p className="swiss-caption mt-7">{t('mvpBuilderProgram.cta.deadline')}</p>
              </div>
            </div>
          </section>

          <Footer />
        </main>
      </div>
    </>
  );
};

export default MVPBuilderProgramPage;
